export interface ApiProblem {
  type: string;
  title: string;
  status: number;
  code: string;
  detail?: string;
  requestId?: string;
}

export class ApiError extends Error {
  public constructor(public readonly problem: ApiProblem) {
    super(localizedProblem(problem));
    this.name = 'ApiError';
  }
}

export interface HttpClientOptions {
  baseUrl?: string;
  readCsrfToken?: () => string | null;
}

export class HttpClient {
  readonly #baseUrl: string;
  readonly #readCsrfToken: () => string | null;

  public constructor(options: HttpClientOptions = {}) {
    this.#baseUrl = options.baseUrl ?? '/api/v1';
    this.#readCsrfToken = options.readCsrfToken ?? (() => null);
  }

  public async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json, application/problem+json');

    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      const csrfToken = this.#readCsrfToken();
      if (csrfToken !== null) headers.set('X-CSRF-Token', csrfToken);
    }

    const response = await fetch(`${this.#baseUrl}${path}`, {
      ...init,
      method,
      headers,
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      const problem = await readProblem(response);
      if (problem.status === 401 && path !== '/auth/login' && path !== '/auth/totp/verify') {
        window.dispatchEvent(new CustomEvent('radio:session-expired'));
      }
      throw new ApiError(problem);
    }

    if (response.status === 204) return undefined as T;

    return (await response.json()) as T;
  }
}

async function readProblem(response: Response): Promise<ApiProblem> {
  try {
    return (await response.json()) as ApiProblem;
  } catch {
    return {
      type: '/problems/request-failed',
      title: 'Запит не виконано',
      status: response.status,
      code: 'request_failed',
    };
  }
}
import { localizedProblem } from '../i18n';
